import { cn } from '@/shared/lib/utils'
import { ComponentProps, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'

type FileDropzoneProps = ComponentProps<'div'> & {
  onFilesAdded: (files: File[]) => void
  maxFiles?: number
  maxFileSize?: number // in MB
  acceptedFileTypes?: string[]
  disabled?: boolean
  children: React.ReactNode
  onFileInputClick: (openFn: () => void) => void
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFilesAdded,
  maxFiles = 5,
  maxFileSize = 10,
  acceptedFileTypes = ['*'],
  disabled = false,
  children,
  onFileInputClick,
  ...props
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validFiles = acceptedFiles.filter((file) => {
        const isValidSize = file.size <= maxFileSize * 1024 * 1024
        if (!isValidSize) {
          console.warn(`File ${file.name} is larger than ${maxFileSize}MB`)
        }
        return isValidSize
      })

      if (validFiles.length > maxFiles) {
        console.warn(`Maximum ${maxFiles} files allowed`)
        validFiles.splice(maxFiles)
      }

      if (validFiles.length > 0) {
        onFilesAdded(validFiles)
      }
    },
    [maxFileSize, maxFiles, onFilesAdded]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: acceptedFileTypes.includes('*')
      ? undefined // Accept all files when '*' is included
      : acceptedFileTypes.reduce((acc, type) => {
          if (type.startsWith('.')) {
            return { ...acc, [type]: [] }
          } else if (type.includes('/')) {
            return { ...acc, [type]: [] }
          } else if (type === 'image') {
            return { ...acc, 'image/*': [] }
          } else if (type === 'video') {
            return { ...acc, 'video/*': [] }
          } else if (type === 'audio') {
            return { ...acc, 'audio/*': [] }
          } else if (type === 'pdf') {
            return { ...acc, 'application/pdf': [] }
          } else {
            return { ...acc, [`.${type}`]: [] }
          }
        }, {}),
    disabled,
    maxFiles,
    preventDropOnDocument: true,
    noClick: true,
    noKeyboard: true,
  })

  useEffect(() => {
    onFileInputClick(open)
  }, [onFileInputClick, open])

  return (
    <div
      {...props}
      {...getRootProps()}
      className={cn(
        isDragActive && 'bg-blue-50/50 outline-2 outline-blue-500',
        props.className
      )}
    >
      <input {...getInputProps()} />
      {children}
    </div>
  )
}
